import { Sprite, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { COLLECTOR_TUNING } from "../tuning/featureTuning";
import { ART_SCALE, COLLECTOR_LAYOUT } from "./layout";

/**
 * The artist's collector (`collector.riv`'s `Collector`, played as its Spine conversion): a round golden frame with four
 * claws holding a crystal ball, red magic swirling in it on a seamless 20 s loop; its `collect` trigger pulses it as a
 * value arrives (0.4 s; from its start again when fired again), `activate` flares it as it starts collecting (1 s).
 */
export const COLLECTOR_RIVE = { collect: "collect", activate: "activate" } as const;

/** What a collector asks of its art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface CollectorArt {
  readonly texture: Texture;
  resize(scale: number): void;
  trigger(name: string): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

/** One collector lent to a collect coin: its art's sprite, centred, at the artboard's size in design units. */
export interface Collector {
  readonly sprite: Sprite;
  /** It starts collecting: the bigger flare. */
  activate(): void;
  /** A value arrives: a pulse. */
  collect(): void;
}

interface Lendable extends Collector {
  readonly art: CollectorArt;
  lent: boolean;
}

/**
 * The collect coins' art (the artist's handoff, 2026-10-12): a few collectors (the file loaded once), each lent to a
 * collect coin while its cell shows one (`CoinSymbolView`), drawn on the UI's runner at the device's loop rate while it
 * is out, and given back when the cell shows another symbol or is put back. A collect coin with none to lend (more than
 * the tuning's on the board at once) shows the delivery's red coin, as every one does before they are in.
 */
export class Collectors {
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  private collectors: Lendable[] | null = null;
  private drawing = false;
  private disposed = false;
  /** How many times a collector has flared, and pulsed. */
  private activations = 0;
  private pulses = 0;
  /** Device pixels per design unit where they are drawn (their textures' size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { ambientRunner: PresentationTaskRunner; log: Log }) {
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
  }

  /** The artist's collectors, loaded. */
  public setArt(arts: readonly CollectorArt[]): void {
    if (this.disposed || this.collectors) {
      for (const art of arts) art.dispose();
      return;
    }
    const { width, height } = COLLECTOR_LAYOUT.artboard;
    this.collectors = arts.map((art): Lendable => {
      art.resize(this.textureScale());
      const sprite = new Sprite({ texture: art.texture, anchor: 0.5, label: "Collector" });
      sprite.setSize(width * ART_SCALE, height * ART_SCALE);
      const activate = (): void => {
        this.activations += 1;
        art.trigger(COLLECTOR_RIVE.activate);
      };
      const collect = (): void => {
        this.pulses += 1;
        art.trigger(COLLECTOR_RIVE.collect);
      };
      return { art, sprite, lent: false, activate, collect };
    });
    this.log("[Collectors] the collectors are in");
  }

  /** Whether they are in, how many are lent now, and how many times one has flared and pulsed (the dev hook's read). */
  public get state(): { loaded: boolean; lent: number; activations: number; pulses: number } {
    const lent = this.collectors?.filter((one) => one.lent).length ?? 0;
    return { loaded: this.collectors !== null, lent, activations: this.activations, pulses: this.pulses };
  }

  /** Device pixels per design unit where they are drawn: their textures are drawn at their size on the screen. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    for (const one of this.collectors ?? []) one.art.resize(this.textureScale());
  }

  /** A collector for a collect coin, idling, drawn from now on; null when none is free or they are not in. */
  public lend(): Collector | null {
    const free = this.collectors?.find((one) => !one.lent);
    if (!free || this.disposed) return null;
    free.lent = true;
    free.sprite.visible = true;
    free.art.render(0);
    this.draw();
    return free;
  }

  /** `collector` back: out of its coin, no longer drawn. */
  public giveBack(collector: Collector): void {
    const one = this.collectors?.find((lendable) => lendable === collector);
    if (!one) return;
    one.lent = false;
    one.sprite.removeFromParent();
  }

  public dispose(): void {
    this.disposed = true;
    const collectors = this.collectors;
    this.collectors = null;
    for (const one of collectors ?? []) {
      one.sprite.destroy();
      one.art.dispose();
    }
  }

  /** Texture pixels per artboard pixel: its size on the screen, at the tuning's share. */
  private textureScale(): number {
    return ART_SCALE * this.pixelsPerUnit * COLLECTOR_TUNING.resolution;
  }

  /** The lent collectors drawn at their own pace, as long as any is out. */
  private draw(): void {
    if (this.drawing) return;
    this.drawing = true;
    let last = 0;
    const out = (): boolean => this.collectors?.some((one) => one.lent) ?? false;
    void this.ambientRunner
      .tickInScope(
        "",
        (elapsed) => {
          const delta = elapsed - last;
          last = elapsed;
          for (const one of this.collectors ?? []) if (one.lent) one.art.advance(delta);
        },
        () => this.disposed || !out(),
      )
      .then(() => {
        this.drawing = false;
        // One lent as the last went back.
        if (!this.disposed && out()) this.draw();
      });
  }
}
